import axios from "axios";

/**
 * Consulta puntual "¿dónde está este artículo?" para UNA combinación
 * artículo+color+talla.
 *
 * Existe para poder explicarle al usuario por qué una nota no lo deja agregar.
 * Antes el rechazo decía sólo "no hay inventario disponible", sin aclarar de qué
 * combinación hablaba ni que la mercancía sí existía pero en otro almacén, y el
 * usuario terminaba capturando stock que ya tenía.
 *
 * Se llama únicamente cuando el rechazo ya ocurrió, no en cada carga de pantalla.
 *
 * Devuelve el arreglo de áreas, o null si la consulta falló (que es distinto de
 * un arreglo vacío, el cual significa "consultado y sin existencia en ningún lado").
 */
export async function obtenerUbicacionArticulo(
  urlInventarios,
  idArticulo,
  idColor,
  idTalla
) {
  if (!idArticulo || !idColor || !idTalla) return null;
  try {
    const res = await axios.get(
      `${urlInventarios}UbicacionArticulo/${idArticulo}/${idColor}/${idTalla}`,
      {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
        timeout: 20000,
      }
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.log("No se pudo consultar la ubicación del artículo:", err);
    return null;
  }
}

/**
 * Otros colores y tallas del MISMO artículo que sí tienen existencia, sacados del
 * inventario que la pantalla ya tiene cargado. No hace ninguna consulta extra.
 *
 * Sirve para que el rechazo no sólo diga "de este color no hay", sino también
 * "de estos otros sí", que es la pregunta que sigue naturalmente.
 */
export function alternativasMismoArticulo(
  inventarios,
  idArticulo,
  idColor,
  idTalla
) {
  if (!Array.isArray(inventarios)) return [];
  const libres = (inv) => Number(inv.disponible ?? inv.cantidad ?? 0);

  return inventarios
    .filter(
      (inv) =>
        String(inv.idArticulo) === String(idArticulo) &&
        libres(inv) > 0 &&
        !(
          String(inv.idColor) === String(idColor) &&
          String(inv.idTalla) === String(idTalla)
        )
    )
    .map((inv) => ({
      color: inv.color,
      talla: inv.talla,
      cantidad: libres(inv),
    }))
    .sort((a, b) => b.cantidad - a.cantidad)
    .slice(0, 8);
}

/**
 * Arma el HTML del mensaje de rechazo.
 *
 * - `ubicacion`: lo que devuelve obtenerUbicacionArticulo.
 * - `idAreaActual` / `nombreAreaActual`: el área desde la que se está vendiendo.
 *   Si no hay área de referencia (un pedido surte de varias bodegas), se omite y
 *   sólo se lista dónde está la mercancía.
 * - `articulo` / `color` / `talla`: para nombrar exactamente qué se rechazó.
 * - `alternativas`: lo que devuelve alternativasMismoArticulo.
 */
export function mensajeUbicacionArticulo({
  ubicacion,
  idAreaActual,
  nombreAreaActual,
  articulo,
  color,
  talla,
  alternativas,
}) {
  const nombreAqui = nombreAreaActual || "Esta área";

  // Encabezado: qué combinación exacta se rechazó.
  let encabezado = "";
  if (articulo || color || talla) {
    const partes = [];
    if (color) partes.push(`color <b>${color}</b>`);
    if (talla) partes.push(`talla <b>${talla}</b>`);
    encabezado =
      `<div style="margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid #eee">` +
      (articulo ? `<b>${articulo}</b>` : "") +
      (partes.length
        ? `${articulo ? "<br>" : ""}${partes.join(" &middot; ")}`
        : "") +
      `</div>`;
  }

  if (ubicacion === null) {
    return (
      `<div style="text-align:left">` +
      encabezado +
      `No se pudo consultar en qué almacén está la mercancía.<br><br>Intenta de nuevo en unos segundos.` +
      `</div>`
    );
  }

  const enTransito = ubicacion.reduce(
    (suma, u) => suma + (u.enTransito || 0),
    0
  );

  const detalle = (u) =>
    `&nbsp;&nbsp;• <b>${u.area}</b>: ${u.disponible} disponible(s)` +
    (u.apartado > 0
      ? ` <span style="color:#888">(${u.fisico} en existencia menos ${u.apartado} apartada(s))</span>`
      : "");

  const bloqueAlternativas = () => {
    if (!alternativas || !alternativas.length) return "";
    let h = `<div style="margin-top:12px;padding-top:8px;border-top:1px solid #eee">`;
    h += `De este mismo artículo sí hay:<br>`;
    alternativas.forEach((a) => {
      h += `&nbsp;&nbsp;• ${a.color || "-"} / ${a.talla || "-"}: ${
        a.cantidad
      } disponible(s)<br>`;
    });
    h += `</div>`;
    return h;
  };

  let html = `<div style="text-align:left">` + encabezado;

  // Sin área de referencia (por ejemplo un pedido, que surte de varias bodegas).
  if (!idAreaActual) {
    const conExistencia = ubicacion
      .filter((u) => u.fisico > 0 || u.apartado > 0)
      .sort((a, b) => b.disponible - a.disponible);
    if (conExistencia.length) {
      html += `No hay disponible de esta combinación. Existencia por almacén:<br>`;
      conExistencia.forEach((u) => {
        html += `${detalle(u)}<br>`;
      });
    } else {
      html += `No hay existencia de esta combinación en ningún almacén.`;
    }
    if (enTransito > 0) {
      html += `<br><span style="color:#888">${enTransito} pieza(s) en tránsito entre almacenes, pendientes de recibir.</span>`;
    }
    html += bloqueAlternativas();
    html += `</div>`;
    return html;
  }

  const aqui = ubicacion.find((u) => String(u.idArea) === String(idAreaActual));
  const disponibleAqui = aqui ? aqui.disponible : 0;

  const otras = ubicacion
    .filter(
      (u) => String(u.idArea) !== String(idAreaActual) && u.disponible > 0
    )
    .sort((a, b) => b.disponible - a.disponible);

  html += `<b>${nombreAqui}:</b> ${disponibleAqui} disponible(s) de esta combinación`;
  if (aqui && aqui.apartado > 0) {
    html += ` <span style="color:#888">(${aqui.fisico} en existencia menos ${aqui.apartado} apartada(s) en pedidos)</span>`;
  }
  html += `<br><br>`;

  if (otras.length) {
    html += `Sí hay en:<br>`;
    otras.forEach((u) => {
      html += `${detalle(u)}<br>`;
    });
    html += `<br>Solicita un traspaso para poder venderlo aquí.`;
  } else {
    html += `No hay existencia de esta combinación en ninguna otra área.`;
  }

  if (enTransito > 0) {
    html += `<br><br><span style="color:#888">${enTransito} pieza(s) en tránsito entre almacenes, pendientes de recibir.</span>`;
  }

  html += bloqueAlternativas();
  html += `</div>`;
  return html;
}
